// MuseScore's engine in WebAssembly (webmscore), served from /webmscore (see scripts/copy-webmscore.js).
// It imports MIDI with MuseScore's importer and engraves MusicXML into SVG pages and PDF.

export interface MeasurePositions {
    elements: Array<{ id: number; page: number; x: number; y: number; sx: number; sy: number }>;
    pageSize: { width: number; height: number };
}

interface WebMscoreScore {
    npages(): Promise<number>;
    saveSvg(page: number, drawPageBackground: boolean): Promise<string>;
    savePdf(): Promise<Uint8Array>;
    saveXml(): Promise<string>;
    measurePositions(): Promise<MeasurePositions>;
    destroy(soft?: boolean): void;
}

interface WebMscoreStatic {
    load(format: "midi" | "musicxml", data: Uint8Array, fonts?: Uint8Array[], doLayout?: boolean): Promise<WebMscoreScore>;
}

declare global {
    interface Window {
        WebMscore?: WebMscoreStatic;
    }
}

const BASE = `${process.env.PUBLIC_URL}/webmscore/`;
// Decoded sizes of webmscore 1.2.1, for progress when the server sends compressed lengths or none
const FILES: Array<[string, number]> = [
    ["webmscore.lib.wasm", 9248386],
    ["webmscore.lib.data", 4099973],
    ["webmscore.lib.mem.wasm", 4152039],
];

export interface EngineStatus {
    state: "idle" | "loading" | "ready" | "failed";
    progress: number;
}

let status: EngineStatus = {state: "idle", progress: 0};
const listeners = new Set<(s: EngineStatus) => void>();
let enginePromise: Promise<WebMscoreStatic | null> | undefined;
let fontPromise: Promise<Uint8Array | null> | undefined;

function setStatus(next: EngineStatus) {
    status = next;
    listeners.forEach(l => l(next));
}

export function subscribeEngineStatus(listener: (s: EngineStatus) => void): () => void {
    listeners.add(listener);
    listener(status);
    return () => listeners.delete(listener);
}

// Downloads a file once so the engine's own request is served from the HTTP cache
async function prefetch(url: string, onBytes: (n: number) => void) {
    const response = await fetch(url);
    if (!response.ok || !response.body) throw new Error(`${url}: ${response.status}`);
    const reader = response.body.getReader();
    for (; ;) {
        const {done, value} = await reader.read();
        if (done) break;
        onBytes(value.byteLength);
    }
}

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`${src} did not load`));
        document.head.appendChild(script);
    });
}

export function loadEngine(): Promise<WebMscoreStatic | null> {
    enginePromise ??= (async () => {
        setStatus({state: "loading", progress: 0});
        try {
            const total = FILES.reduce((sum, [, size]) => sum + size, 0);
            let loaded = 0;
            await Promise.all(FILES.map(([name]) => prefetch(BASE + name, n => {
                loaded += n;
                setStatus({state: "loading", progress: Math.min(0.99, loaded / total)});
            })));
            if (!window.WebMscore) await loadScript(`${BASE}webmscore.js`);
            if (!window.WebMscore) throw new Error("webmscore did not register");
            setStatus({state: "ready", progress: 1});
            return window.WebMscore;
        } catch (e) {
            console.error(e);
            setStatus({state: "failed", progress: 0});
            return null;
        }
    })();
    return enginePromise;
}

// MuseScore's bundled text font has no Cyrillic; DejaVu Serif fills in for titles in Russian
function cyrillicFont(): Promise<Uint8Array | null> {
    fontPromise ??= fetch(`${BASE}DejaVuSerif.ttf`)
        .then(r => (r.ok ? r.arrayBuffer() : Promise.reject(r.status)))
        .then(b => new Uint8Array(b))
        .catch(() => null);
    return fontPromise;
}

async function fontsFor(text: string): Promise<Uint8Array[]> {
    // Anything past Latin Extended (Cyrillic, Greek, ...) needs the extra font
    if (!/[ɐ-￿]/.test(text)) return [];
    const font = await cyrillicFont();
    return font ? [font.slice()] : [];
}

async function engine(): Promise<WebMscoreStatic> {
    const ms = await loadEngine();
    if (!ms) throw new Error("Нотный движок не загрузился.");
    return ms;
}

// Each loaded score runs in its own worker; ending it frees the worker and its memory
function release(score: WebMscoreScore) {
    score.destroy(false);
}

export async function importMidiToXml(bytes: Uint8Array): Promise<string> {
    const ms = await engine();
    // The engine takes ownership of the buffer, so it gets a copy
    const score = await ms.load("midi", bytes.slice());
    try {
        return await score.saveXml();
    } finally {
        release(score);
    }
}

export interface Engraving {
    pages: string[];
    positions: MeasurePositions | null;
    pdf(): Promise<Uint8Array>;
    dispose(): void;
}

export async function engrave(xml: string): Promise<Engraving> {
    const ms = await engine();
    const score = await ms.load("musicxml", new TextEncoder().encode(xml), await fontsFor(xml));
    try {
        const count = await score.npages();
        const pages: string[] = [];
        for (let i = 0; i < count; i++) pages.push(await score.saveSvg(i, true));
        const positions = await score.measurePositions().catch(() => null);
        return {
            pages,
            positions,
            pdf: () => score.savePdf(),
            dispose: () => release(score),
        };
    } catch (e) {
        release(score);
        throw e;
    }
}
